import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, Camera, Search, Download } from 'lucide-react';
import { useVendorContext } from './VendorAdminLayout';
import { vendorAdminApi } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { OrderDetailsDialog } from '../admin/CompletedOrdersPage';
import { toast } from 'sonner';

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

/**
 * VendorCompletedOrdersPage — vendor-scoped view of their delivered orders,
 * including POD photos. Backed by GET /api/vendor-admin/orders?status_f=delivered.
 */
const VendorCompletedOrdersPage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await vendorAdminApi.orders({ status_f: 'delivered' }, impersonating ? vendor_id : undefined);
      setOrders(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor_id]);

  const filtered = orders.filter((o) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return [o.id, o.address, o.driver?.name].filter(Boolean).some((x) => String(x).toLowerCase().includes(needle));
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" /> Completed orders
          </h1>
          <p className="text-slate-500 mt-1">Orders your store has fulfilled, with driver proof-of-delivery photos.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search order or driver…"
              className="pl-8 h-10 w-56"
              data-testid="vendor-completed-search"
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="vendor-completed-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button
            onClick={async () => {
              try {
                const params = impersonating ? { vendor_id, status_f: 'delivered' } : { status_f: 'delivered' };
                const res = await api.get('/vendor-admin/orders/export.csv', { params, responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `shipitnow_completed_orders_${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success('CSV exported');
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Export failed');
              }
            }}
            disabled={loading || orders.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="vendor-completed-export-csv"
          >
            <Download className="h-4 w-4 mr-1.5" />Export CSV
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Delivered" value={orders.length} />
        <StatCard label="Total payout" value={`$${orders.reduce((s, o) => s + Number(o.vendor_amount ?? ((Number(o.subtotal)||0) + (Number(o.tax_amount)||0))), 0).toFixed(2)}`} />
        <StatCard label="With POD photos" value={orders.filter((o) => (o.pod_photos?.length || 0) >= 2).length} />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="vendor-completed-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-3">Order</th>
                <th className="text-left font-medium px-4 py-3">Driver</th>
                <th className="text-left font-medium px-4 py-3">Delivered to</th>
                <th className="text-left font-medium px-4 py-3">Payout</th>
                <th className="text-left font-medium px-4 py-3">Delivered</th>
                <th className="text-left font-medium px-4 py-3">POD</th>
                <th className="text-right font-medium px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10">No completed orders yet.</td></tr>
              ) : filtered.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`vendor-completed-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{o.id}</td>
                  <td className="px-4 py-3 text-slate-900">{o.driver?.name || o.driver_id || '—'}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{o.address || '—'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900" data-testid={`vendor-completed-payout-${o.id}`}>${Number(o.vendor_amount ?? ((Number(o.subtotal)||0) + (Number(o.tax_amount)||0))).toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(o.delivered_at)}</td>
                  <td className="px-4 py-3">
                    {o.pod_skip_reason ? (
                      <Badge className="bg-amber-50 text-amber-700 border-0">Override</Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-700 border-0">{(o.pod_photos?.length || 0)}/2</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(o)} data-testid={`vendor-view-completed-${o.id}`}>
                      <Camera className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OrderDetailsDialog order={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
  </div>
);

export default VendorCompletedOrdersPage;
